import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { usePrompt } from '@/contexts/PromptContext'
import { ApiService } from '@/lib/api'
import { useAutoResizeTextarea } from '@/hooks/useAutoResizeTextarea'

// Extend window interface for timeout and pending save
declare global {
  interface Window {
    contextUpdateTimeout?: NodeJS.Timeout
    contextPendingSave?: () => Promise<void>
  }
}

interface ContextData {
  questions: string[]
  user_input: string
  context_output: string
}

interface ContextStepProps {
  value?: ContextData
  onChange?: (value: ContextData) => void
  readOnly?: boolean
}

const ContextStep: React.FC<ContextStepProps> = ({
  value: externalValue,
  onChange: externalOnChange,
  readOnly = false
}) => {
  const { t } = useTranslation('steps')
  const [internalValue, setInternalValue] = useState<ContextData>({ questions: [], user_input: '', context_output: '' })
  const { promptData, updatePrompt } = usePrompt()

  // Use external value if provided (for refine mode), otherwise use internal state
  const value = externalValue !== undefined ? externalValue : internalValue
  const setValue = externalOnChange || setInternalValue
  const placeholder = t('context.placeholder')
  const textareaRef = useAutoResizeTextarea(value.user_input, placeholder)

  // Load existing context data when promptData is available (only once)
  useEffect(() => {
    if (promptData?.context && !externalValue) {
      // Only load if we don't have any data yet
      setInternalValue(prev => {
        // If we already have questions, don't overwrite
        if (prev.questions.length > 0) {
          return prev
        }
        // Otherwise, load the data from promptData
        return promptData.context
      })
    }
  }, [promptData?.context, externalValue])

  // Questions are now generated immediately when the prompt is created in Home.tsx
  // This component just displays the questions and handles user input


  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (window.contextUpdateTimeout) {
        clearTimeout(window.contextUpdateTimeout)
      }
    }
  }, [])

  // Generate context output when user clicks next
  const generateContextOutput = async () => {
    if (
      promptData?.original_prompt &&
      value.questions.length > 0 &&
      value.user_input.trim().length > 0 &&
      !value.context_output
    ) {
      try {
        const response = await ApiService.generateContextOutput(
          promptData.original_prompt,
          value.questions,
          value.user_input
        )
        if (response.success && response.contextOutput) {
          const newValue = { ...value, context_output: response.contextOutput }
          setValue(newValue)
          // Update the database with the new context output
          await updatePrompt({ context: newValue })
        }
      } catch (error) {
        console.error('Failed to generate context output:', error)
      }
    }
  }

  // Handle user input changes with debounced database updates
  const handleUserInputChange = (userInput: string) => {
    const newValue = { ...value, user_input: userInput }
    setValue(newValue)
    
    // If we're in refine mode (external onChange), don't update database
    if (externalOnChange) return
    
    // Clear any existing timeout
    if (window.contextUpdateTimeout) {
      clearTimeout(window.contextUpdateTimeout)
    }
    
    // Create the save function
    const saveFunction = async () => {
      try {
        await updatePrompt({ context: newValue })
        // Clear the pending save after it completes
        delete window.contextPendingSave
      } catch (error) {
        console.error('Error updating context:', error)
        delete window.contextPendingSave
      }
    }
    
    // Store the save function so it can be executed immediately if needed
    window.contextPendingSave = saveFunction
    
    // Debounce database updates to avoid interfering with typing
    window.contextUpdateTimeout = setTimeout(saveFunction, 1000) // 1 second debounce
  }

  // Expose the context output generation to parent component
  useEffect(() => {
    // Store the function reference globally so parent can call it
    window.contextStepRef = {
      generateContextOutput
    }
    
    return () => {
      // Cleanup on unmount
      delete window.contextStepRef
    }
  }, [generateContextOutput])

  return (
    <div className="text-center">
      <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-4 mt-2 sm:mt-6 px-4">
        {t('context.title')}
      </h2>

      <div className="max-w-2xl mx-auto mb-6 sm:mb-8 px-4">

        {/* Dynamic Context Questions */}
        {value.questions && value.questions.length > 0 ? (
          <>
            <p className="text-gray-600 mb-4 text-xs sm:text-base">
            {t('context.description')}
            </p>
            <ul className="text-left list-disc list-inside space-y-2">
              {value.questions.map((question, index) => (
                <li key={index} className="text-gray-600 text-xs sm:text-base">
                  {question}
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>

      <div className="max-w-2xl mx-auto px-4">
        <textarea
          ref={textareaRef}
          placeholder={t('context.placeholder')}
          value={value.user_input}
          onChange={(e) => handleUserInputChange(e.target.value)}
          readOnly={readOnly}
          className="w-full min-h-12 p-3 text-sm sm:text-lg border border-gray-300 rounded-lg resize-none overflow-y-auto focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
          rows={1}
        />
      </div>
    </div>
  )
}

export default ContextStep
